import { getMomentTz } from "@dexteel/mesf-core";
import { InputBase, MenuItem, Select } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import type { WaterReadType } from "../../models/WaterBilling";
import { WATER_READ_TYPE } from "../../models/WaterBilling";
import { toBillingDate } from "../../utils/billRows";

const INPUT_SX = {
	fontSize: 11,
	"& input": { p: 0, textAlign: "center" },
} as const;

const moment = getMomentTz();

export const NumberCell = ({
	value,
	align = "right",
	min,
	max,
	muted = false,
	onChange,
}: {
	value: number | null;
	align?: "right" | "left";
	min?: number;
	max?: number;
	muted?: boolean;
	onChange: (value: number | null) => void;
}) => (
	<InputBase
		autoComplete="off"
		type="number"
		value={value ?? ""}
		inputProps={{ min, max }}
		onChange={(event) =>
			onChange(event.target.value === "" ? null : Number(event.target.value))
		}
		sx={{
			...INPUT_SX,
			...(muted ? { color: "text.secondary" } : null),
			"& input": { p: 0, textAlign: align },
			width: "100%",
		}}
	/>
);

export const TextCell = ({
	value,
	onChange,
}: {
	value: string | null;
	onChange: (value: string | null) => void;
}) => (
	<InputBase
		autoComplete="off"
		value={value ?? ""}
		onChange={(event) =>
			onChange(event.target.value === "" ? null : event.target.value)
		}
		sx={{ ...INPUT_SX, width: "100%" }}
	/>
);

export const DateCell = ({
	value,
	shouldDisableDate,
	onChange,
}: {
	value: Date | null;
	shouldDisableDate?: (day: Moment) => boolean;
	onChange: (value: Date | null) => void;
}) => (
	<DatePicker
		value={value ? moment.utc(value) : null}
		format="MM/DD/YY"
		shouldDisableDate={shouldDisableDate}
		onChange={(next) => onChange(toBillingDate(next))}
		slotProps={{
			textField: {
				variant: "standard",
				fullWidth: true,
				InputProps: {
					disableUnderline: true,
					sx: {
						...INPUT_SX,
						justifyContent: "center",
						"& input": {
							flex: "none",
							p: 0,
							textAlign: "right",
							width: value ? "8ch" : "10ch",
						},
						"& .MuiInputAdornment-root": { ml: 0 },
					},
				},
			},
			openPickerButton: { size: "small", sx: { p: 0.25 } },
			openPickerIcon: { sx: { fontSize: 14 } },
		}}
	/>
);

export const ReadTypeCell = ({
	value,
	onChange,
}: {
	value: WaterReadType | null;
	onChange: (value: WaterReadType | null) => void;
}) => (
	<Select
		value={value ?? ""}
		variant="standard"
		disableUnderline
		displayEmpty
		fullWidth
		onChange={(event) =>
			onChange(
				event.target.value === ""
					? null
					: (event.target.value as WaterReadType),
			)
		}
		sx={{
			fontSize: 11,
			"& .MuiSelect-select": {
				py: 0,
				pr: "18px !important",
				textAlign: "center",
			},
			"& .MuiSelect-icon": { fontSize: 16, right: 0 },
		}}
	>
		<MenuItem value="">—</MenuItem>
		<MenuItem value={WATER_READ_TYPE.ACTUAL}>Actual</MenuItem>
		<MenuItem value={WATER_READ_TYPE.ESTIMATE}>Estimate</MenuItem>
	</Select>
);
